import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Badge,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Apps as AppsIcon,
  Event as EventIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNotifications } from "../contexts/NotificationContext";

const NotificationDropdown: React.FC = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { notifications, unreadCount, markAsRead } = useNotifications();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "admin":
        return <AppsIcon />;
      case "event":
        return <EventIcon />;
      case "settings":
        return <SettingsIcon />;
      case "success":
        return <CheckCircleIcon />;
      case "error":
        return <ErrorIcon />;
      case "warning":
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "admin":
        return "#e3f2fd"; // Light blue
      case "event":
        return "#e8f5e8"; // Light green
      case "settings":
        return "#e3f2fd"; // Light blue
      case "success":
        return "#e8f5e8"; // Light green
      case "error":
        return "#ffebee"; // Light red
      case "warning":
        return "#fff3e0"; // Light orange
      default:
        return "#f3e5f5"; // Light purple
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleNotificationClick = (notificationId: string) => {
    markAsRead(notificationId);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClose);
    document.addEventListener("touchstart", handleClose);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      document.removeEventListener("touchstart", handleClose);
    };
  }, []);

  return (
    <Box>
      <Tooltip title="Notifications">
        <IconButton
          ref={anchorRef}
          onClick={handleToggle}
          sx={{
            color: "inherit",
            position: "relative",
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {open && (
        <Paper
          sx={{
            position: "absolute",
            top: "100%",
            right: 0,
            mt: 1,
            width: 320,
            maxHeight: 400,
            overflow: "auto",
            zIndex: 2000,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              Notifications
            </Typography>
          </Box>

          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    py: 2,
                    px: 2,
                    cursor: "pointer",
                    backgroundColor:
                      (notification as any).isRead ||
                      (notification as any).is_read
                        ? "transparent"
                        : "action.hover",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => handleNotificationClick(notification.id)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: getNotificationColor(notification.type),
                        color: "primary.main",
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight:
                            (notification as any).isRead ||
                            (notification as any).is_read
                              ? "normal"
                              : "bold",
                          color:
                            (notification as any).isRead ||
                            (notification as any).is_read
                              ? "text.secondary"
                              : "text.primary",
                        }}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mb: 0.5,
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.disabled",
                            fontSize: "0.75rem",
                          }}
                        >
                          {(notification as any).timestamp ||
                            (notification as any).created_at}
                        </Typography>
                      </Box>
                    }
                  />
                  {!(
                    (notification as any).isRead ||
                    (notification as any).is_read
                  ) && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        ml: 1,
                      }}
                    />
                  )}
                </ListItem>
                {index < notifications.length - 1 && <Divider sx={{ mx: 2 }} />}
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                cursor: "pointer",
                textAlign: "center",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Check all notifications &gt;
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default NotificationDropdown;
